import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { X, Clock, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import ResetPasswordModal from './ResetPasswordModal';
import ConfirmResendModal from './ConfirmResendModal';
import ConfirmCancelInviteModal from './ConfirmCancelInviteModal';
import { apiClient } from '../../services/api';

interface UserViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        role: string;
        isActive: boolean;
        organisationId: string;
        invitationToken: string | null;
        invitationExpiry: Date | null;
        invitedBy: string | null;
        lastLogin: Date | null;
        createdAt: Date;
        updatedAt: Date;
    } | null;
    currentUserId: string;
    onEdit?: () => void;
    onDeactivate?: () => void;
    onReactivate?: () => void;
    onRefresh?: () => void;
}

const UserViewModal: React.FC<UserViewModalProps> = ({
    isOpen,
    onClose,
    user,
    currentUserId,
    onEdit,
    onDeactivate,
    onReactivate,
    onRefresh
}) => {
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [showResendConfirm, setShowResendConfirm] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [activities, setActivities] = useState<any[]>([]);
    const [loadingActivities, setLoadingActivities] = useState(false);

    const loadUserActivity = useCallback(async () => {
        if (!user) return;

        setLoadingActivities(true);
        try {
            const response = await apiClient.getUserActivity(user.id, 20);
            setActivities(response.data || []);
        } catch (error) {
            console.error('Failed to load user activity:', error);
            setActivities([]);
        } finally {
            setLoadingActivities(false);
        }
    }, [user]);

    // Fetch user activity when modal opens
    useEffect(() => {
        if (isOpen && user?.id) {
            loadUserActivity();
        }
    }, [isOpen, user, loadUserActivity]);

    if (!isOpen || !user) return null;

    const formatDate = (dateString: string | Date | null): string => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateOnly = (dateString: string | Date | null): string => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatRoleName = (role: string): string => {
        const roleNames: { [key: string]: string } = {
            'super_admin': 'Super Admin',
            'admin': 'Admin',
            'manager': 'Manager',
            'compliance': 'Compliance',
            'user': 'User'
        };
        return roleNames[role] || role;
    };

    const getRoleBadgeClass = (role: string): string => {
        switch (role) {
            case 'super_admin':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'admin':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'manager':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'compliance':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'user':
                return 'bg-gray-100 text-gray-800 border-gray-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getRoleDescription = (role: string): string => {
        const descriptions: { [key: string]: string } = {
            'super_admin': 'Platform-level access across all organisations',
            'admin': 'Full access to manage users, drivers, and perform all operations',
            'manager': 'Can manage drivers and perform licence checks',
            'compliance': 'Read-only access to view drivers and check results',
            'user': 'Can perform licence checks and view assigned drivers'
        };
        return descriptions[role] || 'Standard user permissions';
    };

    const getUserStatus = () => {
        if (!user.isActive) {
            return {
                label: 'Inactive',
                icon: XCircle,
                color: 'text-red-800',
                bgColor: 'bg-red-100',
                borderColor: 'border-red-200'
            };
        }

        if (user.invitationToken && user.invitationExpiry) {
            const now = new Date();
            const expiry = new Date(user.invitationExpiry);
            if (expiry > now) {
                return {
                    label: 'Invitation Pending',
                    icon: Clock,
                    color: 'text-yellow-800',
                    bgColor: 'bg-yellow-100',
                    borderColor: 'border-yellow-200'
                };
            } else {
                return {
                    label: 'Invitation Expired',
                    icon: AlertCircle,
                    color: 'text-orange-800',
                    bgColor: 'bg-orange-100',
                    borderColor: 'border-orange-200'
                };
            }
        }

        return {
            label: 'Active',
            icon: CheckCircle,
            color: 'text-green-800',
            bgColor: 'bg-green-100',
            borderColor: 'border-green-200'
        };
    };

    const formatActionType = (action: string): string => {
        const actionMap: { [key: string]: string } = {
            // Auth
            'LOGIN_SUCCESS': 'Login Successful',
            'LOGIN_FAILED': 'Login Failed',
            'LOGOUT': 'Logged Out',
            'PASSWORD_CHANGED': 'Password Changed',
            'PASSWORD_RESET': 'Password Reset',
            'PASSWORD_RESET_REQUESTED': 'Password Reset Requested',
            'PASSWORD_RESET_COMPLETED': 'Password Reset Completed',
            'ACCOUNT_LOCKED': 'Account Locked',
            'ACCOUNT_UNLOCKED': 'Account Unlocked',
            // User management
            'USER_CREATED': 'User Created',
            'USER_UPDATED': 'Profile Updated',
            'USER_INVITED': 'User Invited',
            'USER_DELETED': 'User Deactivated',
            'USER_DEACTIVATED': 'User Deactivated',
            'USER_ACTIVATED': 'User Reactivated',
            'USER_ROLE_CHANGED': 'Role Changed',
            // Drivers
            'DRIVER_CREATED': 'Driver Added',
            'DRIVER_UPDATED': 'Driver Updated',
            'DRIVER_DELETED': 'Driver Removed',
            'DRIVER_ACTIVATED': 'Driver Reactivated',
            'DRIVER_SITE_CHANGED': 'Driver Site Changed',
            'DRIVER_CONSENT_RECORDED': 'Consent Recorded',
            'DRIVER_CONSENT_WITHDRAWN': 'Consent Withdrawn',
            'CONSENT_REQUEST_SENT': 'Consent Request Sent',
            // Licence checks
            'LICENCE_CHECK_PERFORMED': 'Licence Check Performed',
            'BULK_CHECK_INITIATED': 'Bulk Check Initiated',
            // Sites
            'SITE_CREATED': 'Site Created',
            'SITE_UPDATED': 'Site Updated',
            'SITE_DELETED': 'Site Deleted',
            // Settings
            'SCORING_CONFIG_UPDATED': 'Scoring Config Updated',
            'SCORING_CONFIG_RESET': 'Scoring Config Reset',
            'SCORING_CONFIG_FIELD_RESET': 'Scoring Config Field Reset',
            // Super Admin
            'ORGANISATION_CREATED': 'Organisation Created',
            'ORGANISATION_UPDATED': 'Organisation Updated',
            'ORGANISATION_DELETED': 'Organisation Deactivated',
            'ORGANISATION_REACTIVATED': 'Organisation Reactivated',
        };
        if (actionMap[action]) return actionMap[action];
        return action.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    };

    const formatRelativeTime = (dateString: string | Date): string => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 172800) return 'Yesterday';
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const status = getUserStatus();
    const StatusIcon = status.icon;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                            <span className="text-base font-bold" style={{ color: '#69BE28' }}>
                                {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                            </span>
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-base font-semibold text-gray-900 truncate">
                                {user.firstName} {user.lastName}
                            </h2>
                            <p className="text-sm text-gray-500 truncate">{user.email}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full border ${getRoleBadgeClass(user.role)}`}>
                                    {formatRoleName(user.role)}
                                </span>
                                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${status.bgColor} ${status.color} ${status.borderColor}`}>
                                    <StatusIcon className="w-3 h-3 mr-1" />
                                    {status.label}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors ml-3 flex-shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Personal Information */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Personal Information</h3>
                        <div className="bg-gray-50/80 rounded-lg border border-gray-100 p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">First Name</p>
                                    <p className="text-sm font-medium text-gray-900">{user.firstName}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Last Name</p>
                                    <p className="text-sm font-medium text-gray-900">{user.lastName}</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Email Address</p>
                                <p className="text-sm font-medium text-gray-900 font-mono">{user.email}</p>
                            </div>
                        </div>
                    </div>

                    {/* Role & Permissions */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Role &amp; Permissions</h3>
                        <div className="bg-gray-50/80 rounded-lg border border-gray-100 p-4 space-y-3">
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Current Role</p>
                                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full border ${getRoleBadgeClass(user.role)}`}>
                                    {formatRoleName(user.role)}
                                </span>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Permissions</p>
                                <p className="text-sm text-gray-700">{getRoleDescription(user.role)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Account Information */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Account Information</h3>
                        <div className="bg-gray-50/80 rounded-lg border border-gray-100 p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Account Status</p>
                                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${status.bgColor} ${status.color} ${status.borderColor}`}>
                                        <StatusIcon className="w-3 h-3 mr-1" />
                                        {status.label}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Last Login</p>
                                    <p className="text-sm font-medium text-gray-900">{formatDate(user.lastLogin)}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Account Created</p>
                                    <p className="text-sm font-medium text-gray-900">{formatDateOnly(user.createdAt)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Last Updated</p>
                                    <p className="text-sm font-medium text-gray-900">{formatDateOnly(user.updatedAt)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Invitation Information (if pending) */}
                    {user.invitationToken && (
                        <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Invitation Status</h3>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                                <div className="flex items-start">
                                    <Clock className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-yellow-900">Invitation Pending</p>
                                        <p className="text-xs text-yellow-700 mt-1">
                                            This user has been invited but hasn't accepted the invitation yet.
                                        </p>
                                    </div>
                                </div>
                                {user.invitationExpiry && (
                                    <div className="pt-2 border-t border-yellow-200">
                                        <p className="text-xs text-yellow-700">
                                            <strong>Expires:</strong> {formatDate(user.invitationExpiry)}
                                        </p>
                                    </div>
                                )}
                                <div className="pt-2 border-t border-yellow-200 flex flex-wrap gap-2">
                                    <button
                                        onClick={() => setShowResendConfirm(true)}
                                        className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
                                    >
                                        Resend Invitation
                                    </button>
                                    <button
                                        onClick={() => setShowCancelConfirm(true)}
                                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                                    >
                                        Cancel Invitation
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Activity History */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Recent Activity</h3>
                        <div className="bg-gray-50/80 rounded-lg border border-gray-100 p-4">
                            {loadingActivities ? (
                                <div className="text-center py-4">
                                    <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#69BE28' }} />
                                    <p className="text-sm text-gray-600 mt-2">Loading activity...</p>
                                </div>
                            ) : activities.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No recent activity recorded</p>
                            ) : (
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {activities.map((activity) => (
                                        <div
                                            key={activity.id}
                                            className="flex items-start space-x-3 p-2 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-medium text-gray-900">
                                                        {formatActionType(activity.action)}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {formatRelativeTime(activity.timestamp)}
                                                    </span>
                                                </div>
                                                {activity.user && (
                                                    <p className="text-xs text-gray-500">
                                                        by {activity.user.firstName} {activity.user.lastName}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Quick Actions</h3>
                        <div className="flex flex-wrap gap-2">
                            {/* Edit - Always available */}
                            {onEdit && (
                                <button
                                    onClick={() => {
                                        onEdit();
                                        onClose();
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Edit User
                                </button>
                            )}

                            {/* Reset Password - Only if NOT viewing own account and user is active */}
                            {user.isActive && user.id !== currentUserId && (
                                <button
                                    onClick={() => setShowResetPassword(true)}
                                    className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                                >
                                    Reset Password
                                </button>
                            )}

                            {/* Deactivate - Only if NOT viewing own account */}
                            {user.isActive && user.id !== currentUserId && onDeactivate && (
                                <button
                                    onClick={() => {
                                        onDeactivate();
                                        onClose();
                                    }}
                                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                    Deactivate User
                                </button>
                            )}

                            {/* Reactivate - Only if NOT viewing own account */}
                            {!user.isActive && user.id !== currentUserId && onReactivate && (
                                <button
                                    onClick={() => {
                                        onReactivate();
                                        onClose();
                                    }}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                    Reactivate User
                                </button>
                            )}

                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmResendModal
                isOpen={showResendConfirm}
                onClose={() => setShowResendConfirm(false)}
                onConfirm={async () => {
                    await apiClient.resendInvitation(user.id);
                    toast.success('Invitation resent successfully!');
                    setShowResendConfirm(false);
                    if (onRefresh) onRefresh();
                    onClose();
                }}
                user={{ firstName: user.firstName, lastName: user.lastName, email: user.email }}
            />

            <ConfirmCancelInviteModal
                isOpen={showCancelConfirm}
                onClose={() => setShowCancelConfirm(false)}
                onConfirm={async () => {
                    await apiClient.cancelInvitation(user.id);
                    toast.success('Invitation cancelled successfully.');
                    setShowCancelConfirm(false);
                    if (onRefresh) onRefresh();
                    onClose();
                }}
                user={{ firstName: user.firstName, lastName: user.lastName, email: user.email }}
            />

            {/* Reset Password Modal */}
            {showResetPassword && (
                <ResetPasswordModal
                    user={{
                        id: user.id,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email
                    }}
                    isOpen={showResetPassword}
                    onClose={() => setShowResetPassword(false)}
                    onSuccess={() => {
                        if (onRefresh) {
                            onRefresh();
                        }
                    }}
                />
            )}
        </div>
    );
};

export default UserViewModal;